import { AdService } from '@/modules/adv/ad-service'
import { bannerSequenceConfig } from '@/modules/adv/banner-sequence-config'
import { OutOfPageBannerSequence } from '@/modules/adv/out-of-page-banner-sequence'
import { type GameEvent, PlatformType } from '@/types'
import { EventForLoggersType } from '@/types/logger.types'
import { timer } from '@/utils/helpers'

import { AdvAction, type OutOfPageAdType } from '../../adv'
import EventBus from '../../event-bus'
import {
    AdResponsePayload,
    AdStateChangedAction,
    AdStateChangedEventPayload,
    GameAdHandlerConfig,
    ShortGameEvent,
} from './game-ad-handler.types'

export class GameAdHandler {
    private isShowingFullscreenAd = false

    private banners = new Map<OutOfPageAdType, OutOfPageBannerSequence>()

    private originalMessages = new Map<string, ShortGameEvent>()

    private lastShownTimestamps = new Map<OutOfPageAdType, number>()

    eventBus = new EventBus<GameAdHandlerConfig>()

    constructor(
        private adService: AdService,
        private logEvent: (event: EventForLoggersType) => void,
        private platform: PlatformType,
        private options: {
            skipInterstitials: boolean
        } = {
            skipInterstitials: false,
        },
    ) {}

    async handleMessage(event: GameEvent) {
        this.originalMessages.set(event.data.id, event)

        switch (event.data.action) {
            case 'setBannerAdvSettings':
                this.eventBus.dispatch('adMessage', {
                    action: AdvAction.setBannerAdvSettings,
                    originalMessageEvent: event as GameEvent,
                    payload: {},
                    type: 'adv',
                })
                break
            case 'preloadInterstitial':
                await this.preloadAd('interstitial', event.data.id)
                break
            case 'preloadRewarded':
                await this.preloadAd('rewarded', event.data.id)
                break
            case 'showInterstitial':
                await this.showAd('interstitial', event.data.id)
                break
            case 'showRewarded':
                await this.showAd('rewarded', event.data.id)
                break
            default:
                this.eventBus.dispatch('adMessage', {
                    originalMessageEvent: event as GameEvent,
                    payload: {
                        error: `Unknown message action "${event.data.action}"`,
                    },
                    type: 'error',
                })
                this.originalMessages.delete(event.data.id)
        }
    }

    async showPreroll() {
        const PREROLL_MESSAGE_ID = 'PREROLL_MESSAGE_ID'
        this.originalMessages.set(PREROLL_MESSAGE_ID, {})
        await this.showAd('interstitial_preroll', PREROLL_MESSAGE_ID)
    }

    private validateAdDisplayConditions(type: OutOfPageAdType, messageId: string): boolean {
        const GENERAL_MIN_TIME_BETWEEN_ADS = 5_000 // 5 seconds between ads of the same type
        const MIN_TIME_BETWEEN_PREROLL_AND_INTERSTITIAL = 20_000

        const now = Date.now()

        const lastPrerollCall = this.lastShownTimestamps.get('interstitial_preroll')
        const lastCall = this.lastShownTimestamps.get(type)

        if (type === 'interstitial') {
            if (lastPrerollCall && now - lastPrerollCall < MIN_TIME_BETWEEN_PREROLL_AND_INTERSTITIAL) {
                this.fakeBannerOpening(messageId)
                return false
            }
            if (this.options.skipInterstitials) {
                this.triggerMessage(
                    {
                        status: 'error',
                        error: 'Skip this ad type',
                    },
                    messageId,
                )
                return false
            }
        }

        if (lastCall && now - lastCall < GENERAL_MIN_TIME_BETWEEN_ADS) {
            this.triggerMessage(
                {
                    status: 'error',
                    error: 'Too many requests for this ad type',
                },
                messageId,
            )

            return false
        }

        this.lastShownTimestamps.set(type, now)

        if (this.isShowingFullscreenAd) {
            this.triggerMessage(
                {
                    status: 'error',
                    error: 'Another ad is already open',
                },
                messageId,
            )
            return false
        }

        return true
    }

    private async showAd(type: OutOfPageAdType, messageId: string) {
        if (!this.validateAdDisplayConditions(type, messageId)) {
            return
        }

        this.dispatchAdStateChanged('start', type)

        const delayForLoader: Promise<void> = timer(1800)
        const banner = this.banners.get(type) || (await this.preloadAd(type, messageId))

        if (!banner) {
            this.isShowingFullscreenAd = false
            return
        }
        this.listenToRenderedBannerEvents(banner, type, messageId)

        this.isShowingFullscreenAd = true

        banner.addEventListener('empty', () => {
            // sometimes we don't know a banner is empty till show it.
            // we can't move this code to the general empty banner handler
            // because an ad may be loading while showing another ad
            this.isShowingFullscreenAd = false
            this.triggerMessage({ status: 'close' }, messageId)
        })

        await delayForLoader
        try {
            banner.show()
        } catch {
            this.dispatchAdStateChanged('errored', type)
        }

        this.banners.delete(type)
    }

    private async preloadAd(type: OutOfPageAdType, messageId: string): Promise<OutOfPageBannerSequence | null> {
        try {
            await this.adService.serviceReadyPromise

            if (!this.originalMessages.get(messageId)) {
                throw new Error('Message not found')
            }

            this.triggerMessage({ status: 'start' }, messageId)

            const bannerSequence = new OutOfPageBannerSequence(
                bannerSequenceConfig,
                this.adService,
                type,
                this.platform,
            )

            bannerSequence.addEventListener('empty', () => {
                this.triggerMessage({ status: 'empty' }, messageId)
                this.dispatchAdStateChanged('empty', type)
            })

            await bannerSequence.load()

            this.triggerMessage({ status: 'done' }, messageId)
            this.banners.set(type, bannerSequence)

            return bannerSequence
        } catch {
            this.triggerMessage({ status: 'error', error: 'Cannot preload the ad' }, messageId)
            this.dispatchAdStateChanged('errored', type)
            this.originalMessages.delete(messageId)

            return null
        }
    }

    private listenToRenderedBannerEvents(banner: OutOfPageBannerSequence, type: OutOfPageAdType, messageId: string) {
        const message = this.originalMessages.get(messageId)
        if (!message) {
            return
        }

        banner.addEventListener('rewarded', () => {
            this.triggerMessage({ status: 'rewarded' }, messageId)
        })

        banner.addEventListener('clicked', () => {
            this.triggerMessage({ status: 'clicked' }, messageId)
        })

        banner.addEventListener('timer', (event) => {
            this.dispatchAdStateChanged('timer', type, {
                secondsLeft: event.detail?.secondsLeft,
            })
        })

        banner.addEventListener('closed', ({ detail }) => {
            const now = Date.now()
            this.lastShownTimestamps.set(type, now)

            this.triggerMessage({ status: 'close' }, messageId)
            this.isShowingFullscreenAd = false
            this.originalMessages.delete(messageId)

            this.dispatchAdStateChanged('closed', type)

            if (detail?.banner) {
                this.logEvent({
                    event: 'custom_event',
                    eventName: 'ad_close',
                    adUnit: detail?.banner.adUnit,
                    adPlacement: type,
                })
            }
        })

        banner.addEventListener('rendered', () => {
            this.triggerMessage({ status: 'open' }, messageId)
        })

        banner.addEventListener('viewable', ({ detail }) => {
            this.triggerMessage({ status: 'show' }, messageId)
            this.dispatchAdStateChanged('opened', type, {
                provider: detail?.banner?.provider,
            })
        })

        banner.addEventListener('update', ({ detail }) => {
            this.dispatchAdStateChanged('update', type, {
                totalAds: detail?.totalAds ?? 1,
                currentAd: (detail?.currentAdIndex || 0) + 1,
            })
        })

        banner.addEventListener('banner_closed', ({ detail }) => {
            if (type === 'interstitial_preroll' && detail?.banner) {
                this.logEvent({
                    event: 'custom_event',
                    eventName: 'preroll_end',
                    adUnit: detail?.banner.adUnit,
                    instreamDuration: detail?.banner.instreamDuration,
                    watchedDuration: detail?.banner.watchedDuration,
                })
            }
        })
    }

    private triggerMessage(payload: AdResponsePayload, messageId: string) {
        const message = this.originalMessages.get(messageId)

        if (message?.data) {
            this.eventBus.dispatch('adMessage', {
                action: message.data.action,
                type: 'adv',
                originalMessageEvent: message,
                payload,
            })
        }
    }

    private fakeBannerOpening(id: string) {
        this.triggerMessage({ status: 'start' }, id)
        setTimeout(() => this.triggerMessage({ status: 'done' }, id), 300)
        setTimeout(() => this.triggerMessage({ status: 'open' }, id), 600)
        setTimeout(() => this.triggerMessage({ status: 'show' }, id), 1_000)
        setTimeout(() => {
            this.triggerMessage({ status: 'close' }, id)
            this.originalMessages.delete(id)
        }, 3_000)
    }

    private dispatchAdStateChanged(
        action: AdStateChangedAction,
        adType: OutOfPageAdType,
        message: Omit<AdStateChangedEventPayload, 'action' | 'adType'> = {},
    ) {
        this.eventBus.dispatch('adStateChanged', {
            action,
            adType,
            ...message,
        })
    }
}
